export type FilterDateValue = Date | string | null | undefined;

export interface FilterDateRange {
  startIso: string;
  endExclusiveIso: string;
}

/**
 * Converts user-selected calendar days into the API date-filter contract:
 * start = local midnight of the start day, end = local midnight of the day AFTER the end day (exclusive).
 * Backends must compare with `>= start` and `< end`. Uses calendar-day math so DST days stay correct.
 * Inputs are datepicker `Date`s or ISO strings of them. Returns null when either date is missing or invalid.
 */
export function buildFilterDateRange(start: FilterDateValue, end: FilterDateValue): FilterDateRange | null {
  const startDate = toLocalDayStart(start);
  const endDate = toLocalDayStart(end);
  if (!startDate || !endDate) {
    return null;
  }
  endDate.setDate(endDate.getDate() + 1);
  return { startIso: startDate.toISOString(), endExclusiveIso: endDate.toISOString() };
}

const FILTER_DATE_KEY_PAIRS: readonly [string, string][] = [
  ['startDate', 'endDate'],
  ['transactionStartDate', 'transactionEndDate']
];

/**
 * Returns a copy of an API filter with any user-selected start/end date pair converted via buildFilterDateRange.
 * Apply once, at the service boundary, right before the request is sent.
 */
export function applyFilterDateRange<T extends object>(filter: T): T {
  const requestFilter = { ...filter } as Record<string, FilterDateValue>;
  for (const [startKey, endKey] of FILTER_DATE_KEY_PAIRS) {
    const range = buildFilterDateRange(requestFilter[startKey], requestFilter[endKey]);
    if (range) {
      requestFilter[startKey] = range.startIso;
      requestFilter[endKey] = range.endExclusiveIso;
    }
  }
  return requestFilter as T;
}

/** For endpoints that read `startDate`/`endDate` (ADC/EHR): renames transaction date keys, then applies the date range. */
export function toStartEndFilterDateRange<T extends object>(filter: T): T {
  const requestFilter = { ...filter } as Record<string, unknown>;
  if (requestFilter['transactionStartDate']) {
    requestFilter['startDate'] = requestFilter['transactionStartDate'];
    delete requestFilter['transactionStartDate'];
  }
  if (requestFilter['transactionEndDate']) {
    requestFilter['endDate'] = requestFilter['transactionEndDate'];
    delete requestFilter['transactionEndDate'];
  }
  return applyFilterDateRange(requestFilter) as T;
}

function toLocalDayStart(value: FilterDateValue): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}
